import { useMutation } from "@apollo/client";
import Close from "@mui/icons-material/Close";
import { Box, IconButton, List, ListItem, ListItemText, Tooltip, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { MultipleUserPicker, UserOption } from "components/MultipleUserPicker";
import { StaffRoleNamesEnum } from "encamp-shared/src/constants/permissionsTypes";
import { getFullName } from "encamp-shared/src/utils/name";
import { gql } from "generated-graphql";
import GET_STAFF_ROLES_QUERY, { StaffRoleRow } from "queries/getStaffRoles";
import { useMemo, useState } from "react";

const BULK_ASSIGN_STAFF_ROLE = gql(`
  mutation BulkAssignStaffRole($userIds: [ID!]!, $staffRoleId: ID) {
    bulkAssignStaffRole(userIds: $userIds, staffRoleId: $staffRoleId)
  }
`);

// Stable empty ref: the picker is add-only, so its own value never changes.
const EMPTY_PICKER_VALUE: UserOption[] = [];

type AssignStaffUsersDialogProps = {
  role: StaffRoleRow;
  /** Removed users are reassigned here, so they're never left roleless. */
  standardRoleId: string | undefined;
  onClose: () => void;
  onAssigned: () => void;
};

/** Add/remove staff users for a role. Backed by `bulkAssignStaffRole`. */
export function AssignStaffUsersDialog({
  role,
  standardRoleId,
  onClose,
  onAssigned,
}: AssignStaffUsersDialogProps) {
  const alerts = useAlerts();

  const initialMembers = useMemo<UserOption[]>(
    () =>
      role.users.map((u) => ({
        id: u.id,
        email: u.email,
        name: u.person ? getFullName(u.person) : u.email,
      })),
    [role.users]
  );

  const [members, setMembers] = useState<UserOption[]>(initialMembers);

  // Refetch staff roles so userCount and membership reflect the new assignments.
  const [bulkAssign, { loading }] = useMutation(BULK_ASSIGN_STAFF_ROLE, {
    refetchQueries: [{ query: GET_STAFF_ROLES_QUERY }],
  });

  const initialIds = useMemo(() => new Set(initialMembers.map((u) => u.id)), [initialMembers]);
  const memberIds = useMemo(() => members.map((u) => u.id), [members]);
  const memberIdSet = useMemo(() => new Set(memberIds), [memberIds]);

  // Standard is the floor role, so it has no "remove" target.
  const isStandardRole = role.id === standardRoleId;

  const addedIds = members.filter((u) => !initialIds.has(u.id)).map((u) => u.id);
  const removedIds =
    isStandardRole || !standardRoleId
      ? []
      : initialMembers.filter((u) => !memberIdSet.has(u.id)).map((u) => u.id);

  const hasChanges = addedIds.length > 0 || removedIds.length > 0;

  // Stable, alphabetical member list so rows don't jump as users are added.
  const sortedMembers = useMemo(
    () => [...members].sort((a, b) => a.name.localeCompare(b.name)),
    [members]
  );

  const handleAdd = (selected: UserOption[] | null) => {
    if (!selected?.length) return;
    setMembers((prev) => {
      const existing = new Set(prev.map((m) => m.id));
      const additions = selected.filter((u) => !existing.has(u.id));
      return additions.length > 0 ? [...prev, ...additions] : prev;
    });
  };

  const handleRemove = (id: string) => {
    setMembers((prev) => prev.filter((m) => m.id !== id));
  };

  const handleSave = async () => {
    try {
      let assigned = 0;
      let removed = 0;

      if (addedIds.length > 0) {
        const { data } = await bulkAssign({
          variables: { userIds: addedIds, staffRoleId: role.id },
        });
        assigned = data?.bulkAssignStaffRole ?? 0;
      }

      if (removedIds.length > 0 && standardRoleId) {
        const { data } = await bulkAssign({
          variables: { userIds: removedIds, staffRoleId: standardRoleId },
        });
        removed = data?.bulkAssignStaffRole ?? 0;
      }

      const summary = [
        assigned > 0 ? `assigned ${assigned}` : null,
        removed > 0 ? `returned ${removed} to ${StaffRoleNamesEnum.standard}` : null,
      ]
        .filter(Boolean)
        .join(", ");
      alerts.success(summary ? `${role.name}: ${summary}` : `No changes to ${role.name}`);
      onAssigned();
    } catch (error) {
      alerts.error(error instanceof Error ? error.message : "Failed to update users");
    }
  };

  const description = isStandardRole
    ? "Add staff users to the Standard role. Members leave by being assigned a different role."
    : "Add staff users to this role. Removing a user returns them to the Standard role.";

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      title={`Manage users in ${role.name}`}
      description={description}
      primaryActionButtonProps={{
        loading,
        disabled: !hasChanges,
        onClick: handleSave,
        saveText: "Save",
      }}
    >
      <MultipleUserPicker
        staffOnly
        multiple
        label="Add staff users"
        value={EMPTY_PICKER_VALUE}
        disabledUsersById={memberIds}
        disabledOptionLabel="already a member"
        onChange={handleAdd}
      />

      <Box>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          Members ({members.length})
        </Typography>
        <List
          dense
          disablePadding
          sx={{
            maxHeight: "16rem",
            overflowY: "auto",
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
          }}
        >
          {sortedMembers.length === 0 ? (
            <ListItem>
              <ListItemText
                primaryTypographyProps={{
                  color: "text.secondary",
                  variant: "body2",
                }}
                primary="No users assigned yet."
              />
            </ListItem>
          ) : (
            sortedMembers.map((member) => (
              <ListItem
                key={member.id}
                divider
                secondaryAction={
                  isStandardRole ? undefined : (
                    <Tooltip title="Remove from role">
                      <IconButton
                        edge="end"
                        size="small"
                        aria-label={`Remove ${member.name}`}
                        onClick={() => handleRemove(member.id)}
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )
                }
              >
                <ListItemText
                  primary={member.name}
                  secondary={member.name !== member.email ? member.email : undefined}
                />
              </ListItem>
            ))
          )}
        </List>
      </Box>
    </Dialog>
  );
}
